import React, {
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useHistory } from 'react-router-dom';
import moment from 'moment-timezone';
import format from 'string-template';
import { observer } from 'mobx-react';
import { compose } from 'recompose';
import {
  Alert,
  Avatar,
  Checkbox,
  FormHelperText,
  Divider,
  TextField,
} from '@mui/material';
import { CloseOutlined } from '@mui/icons-material';
import { debounce } from 'throttle-debounce';
import {
  DatePicker,
  TimePicker,
} from '@mui/lab';
import { v4 as uuidv4 } from 'uuid';
import * as Sentry from '@sentry/browser';

import { ChatChannelContext } from '../../../../../../chat';
import { storagePaths, pathPlaceholder } from '../../../../../../utils/firebasePaths';
import { getProcessedAttachmentData } from '../../../../../../utils/attachments';
import {
  combineDateAndTimeWithUTCOffset,
  DateFormat,
  isToday,
} from '../../../../../../utils/date';
import { getUTCOffset, roundToNext5Minutes } from '../../../../../../utils/time';
import useSessionStore from '../../../../../../hooks/useSessionStore';
import useStorage, { StorageProcessState } from '../../../../../../hooks/useStorage';
import useComponentMounted from '../../../../../../hooks/useComponentMounted';
import LoggedInUserContext from '../../../../../../context/LoggedInUserContext';
import ExternalCoachContext from '../../../../../context/ExternalCoachContext';
import LayoutContext from '../../../../../layout/context/LayoutContext';
import { ContainedLoadingOverlay } from '../../../../../components/LoadingOverlay';
import ConfirmDialog from '../../../../../components/ConfirmDialog';
import ToDoList from '../../../../../components/ToDoList';
import { ToDoType } from '../../../../../Model/ToDo';
import CheckinFeedback, { CheckInThemeUsage } from '../../../../../Model/CheckinFeedback';
import UserConfig from '../../../../../../Model/UserConfig';
import FeedContext from '../../../../../context/FeedContext';
import useUpdateInteraction from '../../../../../hooks/useUpdateInteraction';
import useToast from '../../../../../hooks/useToast';
import useLogger from '../../../../../../hooks/useLogger';
import { CoachingActivity } from '../../../../../../utils/log';
import { useCoachConfig } from '../../../../../hooks/useCoachConfig';
import EmojiPickerButton from '../../../../../../components/EmojiInput';
import CheckInInfo from '../../CheckInInfo';
import ReviewRequestModal from '../ReviewRequestModal';
import SmartCheckInResponse from './SmartCheckInResponse/SmartCheckInResponse';
import {
  StyledButton,
  StyledTextArea,
  FeedbackContainer,
  StyledChatIcon,
  StyledInfoText,
  HeaderContainer,
  HeaderTime,
  HeaderTitle,
  NameContainer,
  Container,
  StyledIconButton,
  SmartResponseTitle,
  StyledS2Icon,
  ActionsContainer,
  StyledAttachmentsContainer,
  ScheduleWrapper,
  DateSelectorContainer,
  ButtonContainer,
  CheckBoxContainer,
  TextAreaWrapper,
  EmojiButtonWrapper,
  PanelWrapper,
} from './styles';
import texts from './texts.json';
import {
  scheduleSingleMessage,
  SendStep,
  SendTimeoutError,
  timeStep,
  withTimeout,
} from './util';

const MILLISECONDS_IN_SECOND = 1000;
const MAX_FILE_UPLOAD_SIZE = 50000000; // 50MB
const MAX_FILE_UPLOAD_SIZE_MB = MAX_FILE_UPLOAD_SIZE / 1000000; // bytes to MB
const MAX_MONTH_OF_DATE_SELECTION = 3;
const DRAFT_SAVE_DEBOUNCE_TIME = 500;
// How long the panel waits on sending (after the uploads) before it gives up and lets the coach act again.
const SEND_FEEDBACK_TIMEOUT_MS = 30000;
/*
  Check-ins whose send is still running after the panel stopped waiting on it. Kept outside the panel, so closing
  and reopening it can't send the same feedback twice.
*/
const pendingSendCheckIns = new Set();

const CheckInFeedback = () => {
  const { coachDoc } = useContext(ExternalCoachContext);
  const timezoneOffset = getUTCOffset(coachDoc.timeZone || moment.tz.guess());

  const [feedbackText, setFeedbackText] = useState('');
  const [currentCheckInFeedbackDoc, setCurrentCheckInFeedbackDoc] = useState({});
  const [showAttachmentSaveModal, setShowAttachmentSaveModal] = useState(false);
  const [shouldForceSend, setShouldForceSend] = useState(false);
  const [sendDate, setSendDate] = useState(moment().utcOffset(timezoneOffset));
  const [sendTime, setSendTime] = useState(roundToNext5Minutes(moment().utcOffset(timezoneOffset)));
  const [isLoading, setIsLoading] = useState(false);
  const [showReviewRequestModal, setShowReviewRequestModal] = useState(false);
  const [userConfigDoc, setUserConfigDoc] = useState({});
  const [insertedTheme, setInsertedTheme] = useState('');
  const [hasDraft, setHasDraft] = useState(false);
  const textAreaRef = useRef(null);

  const {
    onMarkAsRead,
    selectedActivity: activity,
    setSelectedActionBarType,
    isSmartFeedbackSelected,
  } = useContext(FeedContext);
  const { showToast } = useToast();
  const isComponentMountedRef = useComponentMounted();
  const {
    uploadAttachments,
    deleteAttachment,
    deleteAttachments,
    createFilesFromDataUrls,
    getBlobUrls,
  } = useStorage();
  const {
    authUser: {
      uid: authUserId,
    } = {},
    isCoachAssistant,
  } = useSessionStore();
  const { userDoc: loggedInUserDoc } = useContext(LoggedInUserContext);
  const { setIsChatPanelOpen } = useContext(LayoutContext);
  const { updateLastInteraction } = useUpdateInteraction(activity.user);

  const [attachments, setAttachments] = useState([]);
  const [exitPath, setExitPath] = useState('');
  const { logCoachingActivity, logEvent } = useLogger();
  const history = useHistory();
  const { sendMessageToUser, sendMessageToChannel, getUserChannelId } = useContext(ChatChannelContext);
  const { coachConfig, loading: isLoadingCoachConfig } = useCoachConfig(activity.coach);
  const [channelId, setChannelId] = useState();
  const [originalAIResponse, setOriginalAIResponse] = useState('');
  const {
    time,
    userName,
    userAvatar,
    user: clientId,
    isFirstCheckIn,
    isFutureStart,
  } = activity;
  // The check-in the panel shows now: the feed stays usable while a send runs, so the coach may open another one.
  const openCheckInIdRef = useRef(activity.checkInId);
  openCheckInIdRef.current = activity.checkInId;

  const saveDraftDebounced = useMemo(() => debounce(
    DRAFT_SAVE_DEBOUNCE_TIME,
    (text, doc, isSmartFeedback, feedbackDocId) => {
      if (doc instanceof CheckinFeedback && doc.id === feedbackDocId) {
        if (isSmartFeedback) {
          doc.update({
            draftSmartFeedbackText: text,
          });
        } else {
          doc.update({
            draftFeedbackText: text,
          });
        }
      }
    },
  ), []);

  // save time spent on each entry to this component + total time spent per check-in
  useEffect(() => {
    const now = new Date();
    const startTime = now.getTime();
    const entry = {
      enteredAt: now,
    };
    return () => {
      const timeSpent = Date.now() - startTime;
      entry.timeSpent = timeSpent / MILLISECONDS_IN_SECOND;
      if (currentCheckInFeedbackDoc instanceof CheckinFeedback) {
        currentCheckInFeedbackDoc.update({
          entriesAt: [
            ...currentCheckInFeedbackDoc.entriesAt,
            entry,
          ],
          totalTimeSpent: currentCheckInFeedbackDoc.totalTimeSpent + entry.timeSpent,
        });
      }
    };
  }, [
    currentCheckInFeedbackDoc,
  ]);

  useEffect(() => {
    if (isFutureStart) {
      logEvent('future_start_checkin_viewed', {
        client_id: clientId,
        ...(isCoachAssistant ? { ac_id: authUserId } : { coach_id: authUserId }),
      });
    }
  }, [isFutureStart, clientId, authUserId, logEvent, isCoachAssistant]);

  // Block navigation if there are unsaved attachments and show the attachment save confirmation modal
  useEffect(() => {
    const unblock = history.block((location) => {
      const newSelectedFile = attachments?.find((file) => !file.storagePath);
      if (!newSelectedFile) {
        return true;
      }
      setShowAttachmentSaveModal(true);
      setExitPath(location.pathname);
      return false;
    });

    return () => {
      unblock();
    };
  }, [
    attachments,
    history,
  ]);

  const activityTime = useMemo(() => (
    moment.utc(time?.toDate()).local().fromNow()
  ), [
    time,
  ]);

  // Save the draft feedback text to the document when the feedback text changes
  useEffect(() => {
    if (currentCheckInFeedbackDoc instanceof CheckinFeedback) {
      saveDraftDebounced(
        feedbackText,
        currentCheckInFeedbackDoc,
        isSmartFeedbackSelected,
        CheckinFeedback.buildId(activity.checkInId, activity.coach),
      );
    }
  }, [
    feedbackText,
    currentCheckInFeedbackDoc,
    saveDraftDebounced,
    isSmartFeedbackSelected,
    activity.checkInId,
    activity.coach,
  ]);

  useEffect(() => () => {
    if (saveDraftDebounced?.cancel) {
      saveDraftDebounced.cancel();
    }
    setFeedbackText('');
  }, [saveDraftDebounced, setFeedbackText]);

  // Load the feedback doc for the current check-in activity
  useEffect(() => {
    // A previous check-in's loads must never land after this one's: feedback goes to what they set.
    let isCurrentCheckIn = true;
    const init = async () => {
      setIsLoading(true);
      setFeedbackText('');
      setHasDraft(false);
      setCurrentCheckInFeedbackDoc({});
      setOriginalAIResponse('');
      setChannelId(undefined);
      try {
        if (activity.checkInId) {
          const [checkInFeedbackDoc, userChannelId, clientConfigDoc] = await Promise.all([
            CheckinFeedback.getByCheckInAndCoach(activity.checkInId, activity.coach),
            getUserChannelId(activity.user),
            UserConfig.getConfigById(activity.user),
          ]);
          if (userChannelId && isCurrentCheckIn) {
            setChannelId(userChannelId);
          }
          if (isComponentMountedRef.current && isCurrentCheckIn) {
            setUserConfigDoc(clientConfigDoc);

            if (checkInFeedbackDoc) {
              setCurrentCheckInFeedbackDoc(checkInFeedbackDoc);
              const draftText = isSmartFeedbackSelected
                ? checkInFeedbackDoc.draftSmartFeedbackText
                : checkInFeedbackDoc.draftFeedbackText;
              if (draftText) {
                setHasDraft(true);
                setFeedbackText(draftText);
              }
              if (checkInFeedbackDoc?.attachments?.length) {
                const blobUrls = await getBlobUrls(checkInFeedbackDoc.attachments);
                if (isComponentMountedRef.current && isCurrentCheckIn) {
                  setAttachments(blobUrls);
                }
              }
            }
          }
        }
      } catch (error) {
        showToast(`${texts.feedbackError}: ${error}`, {
          error: true,
        });
      }
      if (isComponentMountedRef.current && isCurrentCheckIn) {
        setIsLoading(false);
      }
    };
    init();
    return () => {
      isCurrentCheckIn = false;
    };
  }, [
    activity.checkInId,
    activity.coach,
    showToast,
    isComponentMountedRef,
    isSmartFeedbackSelected,
    getBlobUrls,
    getUserChannelId,
    activity.user,
  ]);

  useEffect(() => {
    if (
      !hasDraft
      && !isLoadingCoachConfig
      && coachConfig?.firstCheckInFeedbackTemplate
      && isFirstCheckIn
    ) {
      setFeedbackText(coachConfig.firstCheckInFeedbackTemplate);
    }
  }, [coachConfig, isLoadingCoachConfig, isFirstCheckIn, hasDraft]);

  const minTime = useMemo(() => (
    isToday(moment(sendDate).utcOffset(timezoneOffset)) ? moment().utcOffset(timezoneOffset) : null
  ), [
    sendDate,
    timezoneOffset,
  ]);

  const processAttachments = useCallback(async (attachmentsToProcess) => {
    const uploadedAttachments = [];

    const filesToAddById = attachmentsToProcess.reduce((acc, { file, id }) => {
      const lastDotIndex = file.name.lastIndexOf('.');
      acc[id] = {
        dataUrl: URL.createObjectURL(file),
        format: file.name.slice(lastDotIndex + 1),
        fileName: file.name.slice(0, lastDotIndex),
        fileType: file.type,
        fileId: id,
      };
      return acc;
    }, {});

    const filesToAdd = Object.values(filesToAddById);
    const storagePath = format(storagePaths.CHAT_COLLECTION, {
      [pathPlaceholder.CHANNEL_ID]: channelId,
    });
    const results = await uploadAttachments(
      filesToAdd,
      storagePath,
    );

    const failedFiles = [];
    results.forEach((res) => {
      if (res.state === StorageProcessState.SUCCESS) {
        uploadedAttachments.push(res);
      } else {
        failedFiles.push(res);
      }
    });
    if (failedFiles.length > 0) {
      // If some upload fails, we should stop the sending process
      // and delete the files that were uploaded (if there is any)
      if (uploadedAttachments.length) {
        const filesToDelete = uploadedAttachments.map(({ fileRef }) => ({ storagePath: fileRef }));
        await deleteAttachments(filesToDelete);
      }
      const errorMessage = `Failed to upload ${failedFiles.length} file(s)`;
      throw new Error(errorMessage);
    } else {
      // format the attachment data to be saved in the message document
      return uploadedAttachments.map(({
        fileRef,
        metadata: {
          contentType,
          size,
        },
        fileId,
      }) => getProcessedAttachmentData({
        fileName: filesToAddById[fileId].fileName,
        contentType,
        pathRef: fileRef,
        fileSize: size,
      }));
    }
  }, [
    deleteAttachments,
    uploadAttachments,
    channelId,
  ]);

  const isAIResponseEdited = useCallback((currentText, aiResponse) => {
    if (!aiResponse || currentText === aiResponse) return false;

    let textToCompare = currentText;

    if (insertedTheme) {
      const themeStartPattern = `\n\n${insertedTheme.title}`;
      const themeStartIndex = currentText.indexOf(themeStartPattern);

      if (themeStartIndex !== -1) {
        textToCompare = currentText.substring(0, themeStartIndex);
      }
    }

    return textToCompare !== aiResponse;
  }, [insertedTheme]);

  const getCheckInThemeUsage = useCallback((currentText) => {
    if (!insertedTheme) return CheckInThemeUsage.none;

    const themeStartPattern = `\n\n${insertedTheme.description}`;
    const isThemePresentExactly = currentText.includes(themeStartPattern)
      ? CheckInThemeUsage.original : CheckInThemeUsage.edited;

    return isThemePresentExactly;
  }, [insertedTheme]);

  // send coach feedback to client via in-app chat
  const sendFeedback = useCallback(async (isScheduleMessage) => {
    const sendAt = combineDateAndTimeWithUTCOffset(sendDate, sendTime, timezoneOffset);
    if (isScheduleMessage && sendAt < moment().utcOffset(timezoneOffset)) {
      showToast(texts.passedDateTimeWarning, { error: true });
      return;
    }
    const { checkInId } = activity;
    // A send still running (uploading, or timed out but may still go through) would message the client twice.
    if (pendingSendCheckIns.has(checkInId)) {
      showToast(texts.feedbackStillSending, { error: true });
      return;
    }
    // Held from the start: the coach can open other check-ins and come back while the attachments upload.
    pendingSendCheckIns.add(checkInId);
    let hasStartedDelivery = false;

    setIsLoading(true);
    // Once the send settles, the panel's state is left alone unless it still shows this check-in.
    const isStillOpen = () => isComponentMountedRef.current && openCheckInIdRef.current === checkInId;
    const startedAt = Date.now();
    const progress = { stepDurationsMs: {}, failedStep: '' };
    let hasTimedOut = false;
    const reportFailure = (error, { failedAfterTimeout = false } = {}) => {
      const failure = {
        checkinId: checkInId,
        clientId: activity.user,
        isScheduled: !!isScheduleMessage,
        timedOut: hasTimedOut,
        ...(failedAfterTimeout && { failedAfterTimeout }),
        failedStep: progress.failedStep,
        durationMs: Date.now() - startedAt,
        stepDurationsMs: { ...progress.stepDurationsMs },
      };
      Sentry.captureException(error, {
        extra: {
          message: 'Error while sending check-in feedback',
          ...failure,
        },
      });
      logCoachingActivity(CoachingActivity.CHECKIN_FEEDBACK_SEND_FAILED, failure);
    };
    try {
      const attachmentsInStorage = attachments.filter((file) => file.storagePath);
      const processedAttachments = await timeStep(progress, SendStep.ATTACHMENTS, async () => {
        const otherFiles = attachments.filter((file) => !file.storagePath);
        const storageFiles = await createFilesFromDataUrls(attachmentsInStorage);
        const storageFilesAttachments = storageFiles.map((attachment) => ({
          file: attachment.file,
          id: attachment.fileId,
          url: URL.createObjectURL(attachment.file),
        }));
        return processAttachments([...otherFiles, ...storageFilesAttachments]);
      });
      const formattedMsg = format(texts.feedbackMessage, {
        date: moment(activity.time?.toDate()).format(DateFormat.DEFAULT_DATE_FORMAT),
        feedbackText,
      });

      const deliver = async () => {
        await timeStep(progress, SendStep.MESSAGE, () => {
          if (isScheduleMessage) {
            return scheduleSingleMessage({
              channelId,
              text: formattedMsg,
              userId: activity.coach,
              initiatedBy: authUserId,
              initiatedByName: loggedInUserDoc?.name || '',
              sendAt: sendAt.toDate(),
              activityId: activity.id,
              shouldForceSend,
            }, processedAttachments);
          }
          return channelId
            ? sendMessageToChannel(channelId, formattedMsg, processedAttachments, {}, true)
            : sendMessageToUser(activity.user, formattedMsg, processedAttachments, {}, true);
        });

        /*
          With the message out, archive the check-in (which also removes the claim from the channel), save the
          submitted feedback and record the interaction, all at once.
        */
        let archived = true;
        const followUps = [timeStep(progress, SendStep.ARCHIVE, async () => {
          // A failed archive doesn't fail the send (the message is out); it's logged with the send below
          archived = await onMarkAsRead(activity) !== false;
        })];
        if (currentCheckInFeedbackDoc instanceof CheckinFeedback) {
          const updateData = {
            submittedAt: new Date(),
            submittedBy: authUserId,
          };
          if (isSmartFeedbackSelected) {
            updateData.feedbackText = feedbackText;
            updateData.isSmartFeedbackSelected = true;
          } else {
            updateData.customFeedbackText = feedbackText;
          }
          followUps.push(
            // save final feedback CA submitted along with timestamp
            timeStep(progress, SendStep.FEEDBACK_DOC, async () => {
              await currentCheckInFeedbackDoc.update({
                updateData,
                ...(isSmartFeedbackSelected && {
                  checkInThemeUsage: getCheckInThemeUsage(feedbackText),
                }),
                attachments: [],
              });
              if (isStillOpen()) {
                setAttachments([]);
              }
              // delete files uploaded to the storage as don't need them anymore
              await deleteAttachments(attachmentsInStorage);
            }),
            // Update user's last interaction with the logged in user info
            timeStep(progress, SendStep.LAST_INTERACTION, updateLastInteraction),
          );
        }
        await Promise.all(followUps);

        // Log that feedback was sent, even when it finishes after the panel stopped waiting
        logCoachingActivity(CoachingActivity.CHECKIN_FEEDBACK_SENT, {
          checkinId: checkInId,
          checkinSubmittedAt: activity.checkInData.current.submittedAt.toDate(),
          clientId: activity.user,
          isAIResponse: isSmartFeedbackSelected && !!originalAIResponse,
          isAIResponseEdited: isSmartFeedbackSelected && originalAIResponse
            ? isAIResponseEdited(feedbackText, originalAIResponse) : false,
          durationMs: Date.now() - startedAt,
          stepDurationsMs: { ...progress.stepDurationsMs },
          archived,
        });
      };

      const delivery = deliver();
      hasStartedDelivery = true;
      delivery.then(
        () => pendingSendCheckIns.delete(checkInId),
        (error) => {
          pendingSendCheckIns.delete(checkInId);
          // Once the panel gave up waiting, this is the only report of how the send actually failed
          if (hasTimedOut) {
            reportFailure(error, { failedAfterTimeout: true });
          }
        },
      );
      await withTimeout(delivery, SEND_FEEDBACK_TIMEOUT_MS);

      showToast(isScheduleMessage ? texts.scheduleSuccess : texts.feedbackSuccess);

      if (isStillOpen()) {
        // Show a modal to the AC for requesting app review from the client
        if (isCoachAssistant && userConfigDoc.requestAppReview === undefined) {
          setShowReviewRequestModal(true);
        } else {
          // Only close the action bar if we're not showing the review modal
          setSelectedActionBarType(null);
          setIsChatPanelOpen(false);
        }
      }
    } catch (error) {
      hasTimedOut = error instanceof SendTimeoutError;
      reportFailure(error);

      if (hasTimedOut) {
        showToast(texts.feedbackSendingTimeout, { error: true });
      } else {
        showToast(isScheduleMessage ? texts.feedbackSchedulingError : texts.feedbackSendingError, {
          error: true,
        });
      }
    } finally {
      // A send that never reached the message (e.g. a failed upload) frees the check-in now; a started one when it ends
      if (!hasStartedDelivery) {
        pendingSendCheckIns.delete(checkInId);
      }
      if (isStillOpen()) {
        setIsLoading(false);
      }
    }
  }, [
    feedbackText,
    activity,
    onMarkAsRead,
    showToast,
    currentCheckInFeedbackDoc,
    setSelectedActionBarType,
    authUserId,
    loggedInUserDoc,
    updateLastInteraction,
    isSmartFeedbackSelected,
    logCoachingActivity,
    deleteAttachments,
    createFilesFromDataUrls,
    processAttachments,
    attachments,
    isComponentMountedRef,
    sendDate,
    sendTime,
    timezoneOffset,
    channelId,
    sendMessageToUser,
    sendMessageToChannel,
    shouldForceSend,
    isCoachAssistant,
    userConfigDoc,
    originalAIResponse,
    isAIResponseEdited,
    getCheckInThemeUsage,
    setIsChatPanelOpen,
  ]);

  const handleOnBlur = useCallback(async () => {
    if (currentCheckInFeedbackDoc instanceof CheckinFeedback) {
      if (isSmartFeedbackSelected) {
        await currentCheckInFeedbackDoc.update({
          feedbackText,
        });
      } else {
        await currentCheckInFeedbackDoc.update({
          customFeedbackText: feedbackText,
        });
      }
    }
  }, [
    currentCheckInFeedbackDoc,
    feedbackText,
    isSmartFeedbackSelected,
  ]);

  const onAddAttachments = useCallback(async (event) => {
    const { files } = event.target;
    const validFiles = Object.values(files).every((file) => file.size < MAX_FILE_UPLOAD_SIZE);
    if (validFiles) {
      const finalFileList = Object.values(files).map((file) => (
        {
          file,
          id: `${channelId}-${uuidv4()}`,
          url: URL.createObjectURL(file),
        }
      ));
      setAttachments([...attachments, ...finalFileList]);
    } else {
      const errorMessage = format(texts.fileSizeError, {
        sizeLimit: MAX_FILE_UPLOAD_SIZE_MB,
      });
      showToast(errorMessage, { error: true });
    }
  }, [
    attachments,
    showToast,
    channelId,
  ]);

  const removeFileByIndex = useCallback(async (index) => {
    const fileToRemove = attachments[index];
    const { storagePath = '' } = fileToRemove;

    /**
     * If the file is not in storage, remove it from the attachments list directly otherwise,
     * delete the file from storage and then remove it from the attachments list
     */
    if (!storagePath) {
      const resultAttachments = [...attachments];
      resultAttachments.splice(index, 1);
      setAttachments(resultAttachments);
    } else {
      // Delete the file from storage
      const deleteResult = await deleteAttachment(storagePath);
      if (deleteResult.state === StorageProcessState.SUCCESS) {
        // Remove the file from the attachments list
        const resultAttachments = [...attachments];
        resultAttachments.splice(index, 1);

        if (isComponentMountedRef.current) {
          setAttachments(resultAttachments);
        }

        // Update the document
        const updatedAttachments = currentCheckInFeedbackDoc.attachments.filter(
          (attachment) => attachment.fileRef !== storagePath,
        ).map(({ fileRef, fileId }) => ({
          fileRef,
          fileId,
        }));

        await currentCheckInFeedbackDoc.update({
          attachments: updatedAttachments,
        });
      } else {
        showToast(texts.fileRemoveError, { error: true });
      }
    }
  }, [
    attachments,
    setAttachments,
    currentCheckInFeedbackDoc,
    deleteAttachment,
    showToast,
    isComponentMountedRef,
  ]);

  const onAttachmentSaveAsDraft = useCallback(async () => {
    setIsLoading(true);
    try {
      // Filter out the files that are already uploaded to storage
      const filteredFileDetails = attachments.filter((file) => !file.storagePath).map(({ file, id }) => ({
        dataUrl: URL.createObjectURL(file),
        format: file.name.split('.').pop(),
        fileName: file.name.split('.')[0],
        fileType: file.type,
        fileId: id,
      }));
      // Upload the files to storage
      const uploadResult = await uploadAttachments(filteredFileDetails, storagePaths.CHECK_IN_FEEDBACK);
      const storagePathsOfAttachments = uploadResult.map((file) => ({
        fileRef: file.fileRef,
        fileId: file.fileId,
      }));

      // Update the document with the new attachments
      await currentCheckInFeedbackDoc.update({
        attachments: [...(currentCheckInFeedbackDoc.attachments || []), ...storagePathsOfAttachments],
      });
      if (isComponentMountedRef.current) {
        setShowAttachmentSaveModal(false);
        setIsLoading(false);
      }
      // Unblock navigation and navigate to exitPath
      history.block(() => true);
      history.push(exitPath);
    } catch (error) {
      showToast(texts.fileUploadError, { error: true });
    }
  }, [
    attachments,
    uploadAttachments,
    currentCheckInFeedbackDoc,
    showToast,
    isComponentMountedRef,
    history,
    exitPath,
  ]);

  const onCancelAttachmentSaveAsDraft = useCallback(() => {
    setShowAttachmentSaveModal(false);
    // Unblock navigation and navigate to exitPath
    history.block(() => true);
    history.push(exitPath);
  }, [
    history,
    exitPath,
  ]);

  const onReviewRequestModalClose = useCallback(() => {
    setShowReviewRequestModal(false);
    setSelectedActionBarType(null);
    setIsChatPanelOpen(false);
  }, [setSelectedActionBarType, setIsChatPanelOpen]);

  const handleSmartResponseInsert = useCallback((response, aiResponse, isManualInsert = false) => {
    if (isManualInsert || (feedbackText.trim() === '' && !hasDraft)) {
      setFeedbackText(response);
    }
    setOriginalAIResponse(aiResponse);
  }, [feedbackText, hasDraft]);

  const handleEmojiChange = useCallback(({ emoji }) => {
    const textarea = textAreaRef.current?.querySelector('textarea');
    const cursorPosition = textarea?.selectionStart || feedbackText.length;
    const newValue = `${feedbackText.slice(0, cursorPosition)}${emoji}${feedbackText.slice(cursorPosition)}`;
    setFeedbackText(newValue);
  }, [feedbackText]);

  return (
    <PanelWrapper>
      <FeedbackContainer>
        <HeaderContainer>
          {userAvatar ? <Avatar src={userAvatar} /> : (<Avatar>{userName?.substring(0, 1)}</Avatar>)}
          <NameContainer>
            <Container>
              <HeaderTitle>{userName}</HeaderTitle>
              <CheckInInfo
                isFirstCheckIn={activity?.isFirstCheckIn}
                enabledFeatures={activity?.enabledFeatures}
                weeklyWeightGoal={activity?.weeklyWeightGoal}
              />
            </Container>
            <HeaderTime>{`${activityTime}`}</HeaderTime>
          </NameContainer>
          <StyledIconButton onClick={() => {
            setSelectedActionBarType(null);
            setIsChatPanelOpen(false);
          }}
          >
            <CloseOutlined />
          </StyledIconButton>
        </HeaderContainer>
        {isSmartFeedbackSelected && (
          <ActionsContainer>
            <SmartResponseTitle>
              <StyledS2Icon />
              {texts.title}
            </SmartResponseTitle>
          </ActionsContainer>
        )}

        {isSmartFeedbackSelected && (
          <SmartCheckInResponse
            onInsert={handleSmartResponseInsert}
            checkInId={activity.checkInId}
            coachId={activity.coach}
            onThemeInsert={(theme) => setInsertedTheme(theme)}
          />
        )}
        <TextAreaWrapper>
          <StyledTextArea
            value={feedbackText}
            onChange={(e) => setFeedbackText(e.target.value)}
            onBlur={handleOnBlur}
            minRows={10}
            maxRows={20}
            placeholder={texts.feedbackPlaceholder}
            multiline
            variant="outlined"
            ref={textAreaRef}
          />
          <EmojiButtonWrapper>
            <EmojiPickerButton
              onChange={handleEmojiChange}
              coachDoc={coachDoc}
            />
          </EmojiButtonWrapper>
        </TextAreaWrapper>

        <StyledAttachmentsContainer
          fileAttachments={attachments}
          onFilesSelected={onAddAttachments}
          onFileRemoved={removeFileByIndex}
          // The panel's own loader covers the upload; the container's would dim the whole page
          isUploading={false}
        />
        {isSmartFeedbackSelected && (
          <StyledInfoText>
            {texts.disclaimer}
          </StyledInfoText>
        )}
        <ScheduleWrapper>
          {!!coachDoc.timeZone && (
            <Alert severity="warning">
              {texts.scheduleWarningMessage}
            </Alert>
          )}
          <CheckBoxContainer>
            <Checkbox
              checked={shouldForceSend}
              onChange={() => setShouldForceSend((prev) => !prev)}
            />
            <FormHelperText>{texts.forceSendLabel}</FormHelperText>
          </CheckBoxContainer>
          <DateSelectorContainer>
            <DatePicker
              label={texts.selectDate}
              value={sendDate}
              onChange={setSendDate}
              minDate={moment().utcOffset(timezoneOffset)}
              maxDate={moment().utcOffset(timezoneOffset).add(MAX_MONTH_OF_DATE_SELECTION, 'months')}
              renderInput={(params) => (
                <TextField {...params} />
              )}
            />
            <TimePicker
              label={texts.selectTime}
              value={sendTime}
              onChange={setSendTime}
              minTime={minTime}
              minutesStep={5}
              renderInput={(params) => (
                <TextField {...params} />
              )}
            />
          </DateSelectorContainer>
          <ButtonContainer>
            <StyledButton
              onClick={() => sendFeedback(false)}
              disabled={!feedbackText}
            >
              <StyledChatIcon />
              {texts.sendFeedBack}
            </StyledButton>
            <StyledButton
              onClick={() => sendFeedback(true)}
              disabled={!feedbackText}
            >
              <StyledChatIcon />
              {texts.scheduleMessage}
            </StyledButton>
          </ButtonContainer>
          <Divider flexItem />
          <ToDoList clientId={clientId} coachId={coachDoc.id} todoType={ToDoType.CHECKIN} />
        </ScheduleWrapper>
        <ConfirmDialog
          isOpen={showAttachmentSaveModal}
          onCancel={onCancelAttachmentSaveAsDraft}
          onConfirm={onAttachmentSaveAsDraft}
          dialogTexts={{
            title: texts.attachmentSaveTitle,
          }}
        />
        {showReviewRequestModal && (
          <ReviewRequestModal
            clientConfigDoc={userConfigDoc}
            activity={activity}
            showModal={showReviewRequestModal}
            onClose={onReviewRequestModalClose}
          />
        )}
      </FeedbackContainer>
      <ContainedLoadingOverlay isLoading={isLoading} />
    </PanelWrapper>
  );
};

export default compose(
  observer,
)(CheckInFeedback);
